"use client";

import { useEffect, useRef, useState } from "react";

import {
  type EmbedPlatform,
  getEmbedHeight,
  parseEmbedUrl,
} from "@/lib/embed-parsing";

interface EmbedPlayerProps {
  audioUrl: string;
  audioMimeType: string;
  title?: string | null;
  compact?: boolean;
}

const PLATFORM_NAMES: Record<EmbedPlatform, string> = {
  soundcloud: "SoundCloud",
  spotify: "Spotify",
  youtube: "YouTube",
  apple_music: "Apple Music",
  deezer: "Deezer",
  tidal: "Tidal",
  traxsource: "Traxsource",
};

const PLATFORM_DOT_COLORS: Record<EmbedPlatform, string> = {
  soundcloud: "#ff5500",
  spotify: "#1db954",
  youtube: "#ff0000",
  apple_music: "#fa57c1",
  deezer: "#a238ff",
  tidal: "#00ffff",
  traxsource: "#ef4923",
};

function getCompactHeight(platform: EmbedPlatform): number {
  // SoundCloud's widget needs its full 166px to show artwork + waveform;
  // anything shorter crops the player controls.
  if (platform === "soundcloud") {
    return 166;
  }
  if (platform === "spotify") {
    return 80;
  }
  return 56;
}

export default function EmbedPlayer({
  audioUrl,
  title,
  compact = false,
}: EmbedPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Only mount the third-party iframe once the player is near the viewport, so
  // a feed with many embeds doesn't load N iframes at once.
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible]);

  const embedData = parseEmbedUrl(audioUrl);

  if (!embedData) {
    return (
      <div className="w-full">
        <a
          href={audioUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          External audio link
        </a>
      </div>
    );
  }

  const { platform, embedUrl } = embedData;
  const platformName = PLATFORM_NAMES[platform];
  const dotColor = PLATFORM_DOT_COLORS[platform];
  // Video embeds size by aspect ratio; fixed heights squash the player.
  const isVideo = platform === "youtube";
  const height = compact
    ? getCompactHeight(platform)
    : getEmbedHeight(platform, audioUrl);

  return (
    <div className="w-full" ref={containerRef}>
      <div className="mb-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
        {platformName}
      </div>
      {isVisible ? (
        <iframe
          src={embedUrl}
          {...(isVideo ? {} : { height })}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          className={
            isVideo
              ? "aspect-video w-full rounded-lg border-0"
              : "w-full rounded-lg border-0"
          }
          title={title ?? `${platformName} embed`}
        />
      ) : (
        <div
          className={
            isVideo
              ? "aspect-video w-full animate-pulse rounded-lg bg-muted"
              : "w-full animate-pulse rounded-lg bg-muted"
          }
          style={isVideo ? undefined : { height }}
        />
      )}
    </div>
  );
}
